import { fromJS, List, Map } from 'immutable';

import { addTimedAlert } from 'components/shared/Alert';
import { PERCENT_OFF } from 'constants/campaigns';
import CampaignRecord from 'records/campaign';
import EmailSettingsRecord from 'records/emailSettings';
import ListingRecord from 'records/listing';
import baseReducer from 'redux/baseReducer';
import { types as emailSettingsTypes } from 'redux/emailSettings/reducer';
import { types as listingTypes } from 'redux/listing/reducer';
import {
    UPDATE_PAGE,
    UPDATE_PAGE_FAIL,
    UPDATE_PAGE_SUCCESS,
} from 'redux/page/reducer';
import { types as productTypes } from 'redux/product/reducer';
import { createSymbols, formatPriceIfNeeded } from 'utils/helpers';
import { mapCampaign, mapPage, mapProduct } from 'utils/mapToRecord';

export const {
    CREATE_CAMPAIGN,
    CREATE_CAMPAIGN_FAIL,
    CREATE_CAMPAIGN_SUCCESS,
    CREATE_MULTI_USE_PROMOTION,
    CREATE_MULTI_USE_PROMOTION_FAIL,
    CREATE_MULTI_USE_PROMOTION_SUCCESS,
    CREATE_PROMOTIONS,
    CREATE_PROMOTIONS_FAIL,
    CREATE_PROMOTIONS_SUCCESS,
    DELETE_MULTI_USE_PROMOTION,
    DELETE_MULTI_USE_PROMOTION_FAIL,
    DELETE_MULTI_USE_PROMOTION_SUCCESS,
    DELETE_SINGLE_USE_PROMOTION,
    DELETE_SINGLE_USE_PROMOTION_FAIL,
    DELETE_SINGLE_USE_PROMOTION_SUCCESS,
    EDIT_CAMPAIGN,
    EDIT_IN_CAMPAIGN,
    EDIT_MULTI_USE_PROMOTION,
    FAIL_CAMPAIGN,
    FETCH_CAMPAIGN,
    FETCH_CAMPAIGN_FAIL,
    FETCH_CAMPAIGN_STATS,
    FETCH_CAMPAIGN_STATS_FAIL,
    FETCH_CAMPAIGN_STATS_SUCCESS,
    FETCH_CAMPAIGN_SUCCESS,
    FUND_CAMPAIGN,
    FUND_CAMPAIGN_FAIL,
    FUND_CAMPAIGN_SUCCESS,
    RESET_CAMPAIGN,
    TOGGLE_EDIT_MULTI_USE_PROMOTION,
    UPDATE_CAMPAIGN,
    UPDATE_CAMPAIGN_FAIL,
    UPDATE_CAMPAIGN_SUCCESS,
    UPDATE_CAMPAIGN_DISCOUNT_TYPE,
    UPDATE_CAMPAIGN_PERCENT_OFF,
    UPDATE_CAMPAIGN_TYPE,
    UPDATE_MULTI_USE_PROMOTION,
    UPDATE_MULTI_USE_PROMOTION_FAIL,
    UPDATE_MULTI_USE_PROMOTION_SUCCESS,
    VERIFY_BOOST,
    VERIFY_BOOST_FAIL,
    VERIFY_BOOST_SUCCESS,
} = createSymbols([
    'CREATE_CAMPAIGN',
    'CREATE_CAMPAIGN_FAIL',
    'CREATE_CAMPAIGN_SUCCESS',
    'CREATE_MULTI_USE_PROMOTION',
    'CREATE_MULTI_USE_PROMOTION_FAIL',
    'CREATE_MULTI_USE_PROMOTION_SUCCESS',
    'CREATE_PROMOTIONS',
    'CREATE_PROMOTIONS_FAIL',
    'CREATE_PROMOTIONS_SUCCESS',
    'DELETE_MULTI_USE_PROMOTION',
    'DELETE_MULTI_USE_PROMOTION_FAIL',
    'DELETE_MULTI_USE_PROMOTION_SUCCESS',
    'DELETE_SINGLE_USE_PROMOTION',
    'DELETE_SINGLE_USE_PROMOTION_FAIL',
    'DELETE_SINGLE_USE_PROMOTION_SUCCESS',
    'EDIT_CAMPAIGN',
    'EDIT_IN_CAMPAIGN',
    'EDIT_MULTI_USE_PROMOTION',
    'FAIL_CAMPAIGN',
    'FETCH_CAMPAIGN',
    'FETCH_CAMPAIGN_FAIL',
    'FETCH_CAMPAIGN_STATS',
    'FETCH_CAMPAIGN_STATS_FAIL',
    'FETCH_CAMPAIGN_STATS_SUCCESS',
    'FETCH_CAMPAIGN_SUCCESS',
    'FUND_CAMPAIGN',
    'FUND_CAMPAIGN_FAIL',
    'FUND_CAMPAIGN_SUCCESS',
    'RESET_CAMPAIGN',
    'TOGGLE_EDIT_MULTI_USE_PROMOTION',
    'UPDATE_CAMPAIGN',
    'UPDATE_CAMPAIGN_FAIL',
    'UPDATE_CAMPAIGN_SUCCESS',
    'UPDATE_CAMPAIGN_DISCOUNT_TYPE',
    'UPDATE_CAMPAIGN_PERCENT_OFF',
    'UPDATE_CAMPAIGN_TYPE',
    'UPDATE_MULTI_USE_PROMOTION',
    'UPDATE_MULTI_USE_PROMOTION_FAIL',
    'UPDATE_MULTI_USE_PROMOTION_SUCCESS',
    'VERIFY_BOOST',
    'VERIFY_BOOST_FAIL',
    'VERIFY_BOOST_SUCCESS',
]);

export const initialState = fromJS({
    boostVerification: {},
    creationType: null,
    customizePage: false,
    data: CampaignRecord(),
    disableSave: false,
    discountType: PERCENT_OFF,
    errors: {},
    hasFetched: false,
    isBoosted: false,
    isEditingMultiUsePromotion: false,
    isFetching: false,
    isSaving: false,
    mediaDraggingIndex: null,
    mediaUploadType: null,
    percentOff: '1',
    pluginState: {},
    richTextEditor: null,
});

const updatePercentOffIfNecessary = (campaign, prevState) => {
    const listingPrice = campaign.getIn(['listing', 'price']);
    const discountPrice = campaign.get('discountPrice');
    if (listingPrice && discountPrice) {
        prevState.set(
            'percentOff',
            (((listingPrice - discountPrice) / listingPrice) * 100).toFixed(0)
        );
    }
};

const formatDiscountPrice = (campaign) => campaign.set(
    'discountPrice',
    formatPriceIfNeeded(campaign.get('discountPrice'))
);

const updateFieldIfCampaignIdMatches = (state, map, field) => {
    return state.getIn(['data', 'id']) === map.get('campaignId')
        ? state.set('isSaving', false).setIn(['data', field], map)
        : state.set('isSaving', false);
};

const createMultiUsePromotionSuccess = (state, { payload }) => state.setIn(
    ['data', 'multiUsePromotion'],
    fromJS(payload.data)
);
const createPromotionsSuccess = (state, { payload }) => {
    const promotions = fromJS(payload.data) || List([]);

    return state.getIn(['data', 'id']) === promotions.getIn([0, 'campaignId'])
        ? state.setIn(['data', 'singleUsePromotions'], promotions)
        : state;
};
const deleteMultiUsePromotionSuccess = (state, { meta }) => {
    if (state.getIn(['data', 'id']) === meta.previousAction.promotion.get('campaignId')) {
        return state.setIn(['data', 'multiUsePromotion'], fromJS({}));
    }

    return state;
};
const deletePromotionFail = (state, { meta }) => {
    addTimedAlert(meta.previousAction.failMessage);

    return state;
};
const deleteSingleUsePromotionSuccess = (state, { meta }) => {
    const { promotion } = meta.previousAction;
    const promotionsPath = ['data', 'singleUsePromotions'];

    if (
        state.getIn(['data', 'id'])
        === promotion.get('campaignId')
    ) {
        return state.setIn(
            promotionsPath,
            state.getIn(promotionsPath).filter(
                (p) => p.get('id') !== promotion.get('id')
            )
        );
    }

    return state;
};
const editCampaign = (state, { field, value }) => state.setIn(['data', field], value);
const editInCampaign = (state, { path, value }) => state.setIn(path, value);
const editMultiUsePromotion = (state, { field, value }) => {
    const multiUsePromotion = (
        state.getIn(['data', 'multiUsePromotion'])
        || Map()
    ).set(field, value);

    return state.setIn(['data', 'multiUsePromotion'], multiUsePromotion);
};
const fetchCampaign = (state) => state.set('isFetching', true);
const fetchCampaignStatsSuccess = (state, { payload }) => state.withMutations((prevState) => {
    return prevState.set('isFetchingStats', false)
        .set('stats', fromJS(payload.data));
});
const resetCampaign = () => initialState;
const savingFalse = (state) => state.set('isSaving', false);
const savingTrue = (state) => state.set('isSaving', true);
const setCampaignSuccess = (state, { payload }) => state.withMutations((prevState) => {
    const campaign = formatDiscountPrice(fromJS(payload.data));

    prevState
        .set('isBoosted', Boolean(campaign.get('boost')))
        .set('isFetching', false)
        .set('isSaving', false)
        .set('hasFetched', true)
        .set('rebatePrice', campaign.getIn(['multiUsePromotion', 'payload']))
        .set('data', mapCampaign(campaign));

    if (campaign.getIn(['singleUsePromotions', 0]) || campaign.get('multiUsePromotion')) {
        prevState.setIn(['data', 'maxQuantityDisclaimer'], true);
    }

    updatePercentOffIfNecessary(campaign, prevState);
});
const setErrors = (state, { error }) => {
    return state.set('errors', fromJS(error.response.data)).set('isSaving', false);
};
const toggleEditMultiUsePromotion = (state) => state.set(
    'isEditingMultiUsePromotion',
    !state.get('isEditingMultiUsePromotion')
);
const updateCampaignDiscountType = (state, { discountType }) => state.set(
    'discountType',
    discountType
);
const updateCampaignType = (state, { campaignType }) => state.set(
    'campaignType',
    campaignType
);
const updateCampaignPercentOff = (state, { percent }) => state.set('percentOff', percent);
const updateEmailSettingsSuccess = (state, { payload }) => updateFieldIfCampaignIdMatches(
    state,
    EmailSettingsRecord(payload.data),
    'emailSettings'
);
const updateListingSuccess = (state, { payload }) => updateFieldIfCampaignIdMatches(
    state,
    ListingRecord(payload.data),
    'listing'
);
const updateMultiUsePromotionSuccess = (state, { payload }) => state.setIn(
    ['data', 'multiUsePromotion'],
    fromJS(payload.data)
);
const updatePageSuccess = (state, { payload }) => updateFieldIfCampaignIdMatches(
    state,
    mapPage(payload.data),
    'page'
);
const updateProductSuccess = (state, { payload }) => updateFieldIfCampaignIdMatches(
    state,
    mapProduct(payload.data),
    'product'
);
const verifyBoostSuccess = (state, { payload }) => {
    return state.set('boostVerification', fromJS(payload.data));
};

export default baseReducer(initialState, {
    [CREATE_CAMPAIGN]: savingTrue,
    [CREATE_CAMPAIGN_FAIL]: savingFalse,
    [CREATE_CAMPAIGN_SUCCESS]: setCampaignSuccess,
    [CREATE_MULTI_USE_PROMOTION_SUCCESS]: createMultiUsePromotionSuccess,
    [CREATE_PROMOTIONS_SUCCESS]: createPromotionsSuccess,
    [DELETE_MULTI_USE_PROMOTION_FAIL]: deletePromotionFail,
    [DELETE_MULTI_USE_PROMOTION_SUCCESS]: deleteMultiUsePromotionSuccess,
    [DELETE_SINGLE_USE_PROMOTION_FAIL]: deletePromotionFail,
    [DELETE_SINGLE_USE_PROMOTION_SUCCESS]: deleteSingleUsePromotionSuccess,
    [EDIT_CAMPAIGN]: editCampaign,
    [EDIT_IN_CAMPAIGN]: editInCampaign,
    [EDIT_MULTI_USE_PROMOTION]: editMultiUsePromotion,
    [emailSettingsTypes.POST]: savingTrue,
    [emailSettingsTypes.POST_FAIL]: savingFalse,
    [emailSettingsTypes.POST_SUCCESS]: updateEmailSettingsSuccess,
    [FAIL_CAMPAIGN]: setErrors,
    [FETCH_CAMPAIGN]: fetchCampaign,
    [FETCH_CAMPAIGN_FAIL]: setErrors,
    [FETCH_CAMPAIGN_STATS_FAIL]: setErrors,
    [FETCH_CAMPAIGN_STATS_SUCCESS]: fetchCampaignStatsSuccess,
    [FETCH_CAMPAIGN_SUCCESS]: setCampaignSuccess,
    [listingTypes.PUT]: savingTrue,
    [listingTypes.PUT_FAIL]: savingFalse,
    [listingTypes.PUT_SUCCESS]: updateListingSuccess,
    [productTypes.PUT]: savingTrue,
    [productTypes.PUT_FAIL]: savingFalse,
    [productTypes.PUT_SUCCESS]: updateProductSuccess,
    [RESET_CAMPAIGN]: resetCampaign,
    [TOGGLE_EDIT_MULTI_USE_PROMOTION]: toggleEditMultiUsePromotion,
    [UPDATE_CAMPAIGN]: savingTrue,
    [UPDATE_CAMPAIGN_DISCOUNT_TYPE]: updateCampaignDiscountType,
    [UPDATE_CAMPAIGN_FAIL]: setErrors,
    [UPDATE_CAMPAIGN_PERCENT_OFF]: updateCampaignPercentOff,
    [UPDATE_CAMPAIGN_SUCCESS]: setCampaignSuccess,
    [UPDATE_CAMPAIGN_TYPE]: updateCampaignType,
    [UPDATE_MULTI_USE_PROMOTION_SUCCESS]: updateMultiUsePromotionSuccess,
    [UPDATE_PAGE]: savingTrue,
    [UPDATE_PAGE_FAIL]: savingFalse,
    [UPDATE_PAGE_SUCCESS]: updatePageSuccess,
    [VERIFY_BOOST]: savingTrue,
    [VERIFY_BOOST_FAIL]: savingFalse,
    [VERIFY_BOOST_SUCCESS]: verifyBoostSuccess,
});
